import { useEffect, useState, useCallback } from "react";
import api, { eur, hrs } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader, KpiCard } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function Line({ label, value, bold, tone }) {
  return (
    <div className={`flex justify-between py-1.5 border-b border-zinc-50 text-sm ${bold ? "font-bold" : ""}`}>
      <span className={bold ? "text-zinc-900" : "text-zinc-600"}>{label}</span>
      <span className={`num ${tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-rose-600" : ""}`}>{value}</span>
    </div>
  );
}

export default function SiteProfitability() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [ovh, setOvh] = useState("");
  const [companyOvh, setCompanyOvh] = useState(0);
  const [savingOvh, setSavingOvh] = useState(false);

  useEffect(() => { api.get("/sites").then((r) => { setSites(r.data); if (r.data[0]) setSiteId(r.data[0].id); }); }, []);
  const loadPnl = useCallback(() => {
    if (!siteId) return;
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    api.get(`/sites/${siteId}/profitability`, { params }).then((r) => setData(r.data));
    api.get("/settings").then((r) => { const v = r.data.overhead_pct ?? 0; setOvh(String(v)); setCompanyOvh(Number(v)); });
  }, [siteId, range]);
  useEffect(() => { loadPnl(); }, [loadPnl]);

  const saveOverhead = async () => {
    setSavingOvh(true);
    try { await api.put("/settings", { overhead_pct: Number(ovh) }); toast.success("Company running cost updated"); loadPnl(); }
    catch { toast.error("Could not save running cost"); }
    finally { setSavingOvh(false); }
  };

  const p = data?.pnl;
  return (
    <AdminLayout nav={adminNav} title="Site Profitability">
      <PageHeader title="Site Profit & Loss" subtitle="Fully-loaded labour + materials profitability" />
      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div className="min-w-[240px]">
          <Label>Site</Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger data-testid="pnl-site" className="mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>From</Label><Input data-testid="pnl-start" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="mt-1" /></div>
        <div><Label>To</Label><Input data-testid="pnl-end" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="mt-1" /></div>
      </div>

      {p && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard testId="pnl-revenue" label="Total Revenue" value={eur(p.total_revenue)} />
            <KpiCard testId="pnl-cost" label="Total Cost" value={eur(p.total_cost)} />
            <KpiCard testId="pnl-profit" label="Gross Profit" value={eur(p.gross_profit)} accent />
            <KpiCard testId="pnl-margin" label="Gross Margin" value={`${hrs(p.gross_margin_pct)}%`} accent />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Revenue</h3>
              <Line label="Standard labour revenue" value={eur(p.labour_revenue)} />
              <Line label="Overtime revenue" value={eur(p.overtime_revenue)} />
              <Line label="Materials revenue" value={eur(p.materials_revenue)} />
              <Line label="Other billable revenue" value={eur(p.other_revenue)} />
              <Line label="Total revenue" value={eur(p.total_revenue)} bold />
            </div>
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Costs</h3>
                <div className="flex items-center gap-1.5">
                  <Label className="text-[11px] text-zinc-500 whitespace-nowrap">Company default cost</Label>
                  <Input data-testid="overhead-pct" type="number" step="0.5" min="0" value={ovh} onChange={(e) => setOvh(e.target.value)} className="h-8 w-16" />
                  <span className="text-sm text-zinc-500">%</span>
                  <Button data-testid="overhead-save" size="sm" className="h-8 bg-slate-900 text-white" disabled={savingOvh || Number(ovh) === companyOvh} onClick={saveOverhead}>Save</Button>
                </div>
              </div>
              <Line label="Basic wages" value={eur(p.basic_wages)} />
              <Line label="Overtime wages" value={eur(p.overtime_wages)} />
              <Line label="Employer PRSI" value={eur(p.employer_prsi)} />
              <Line label="Holiday accrual" value={eur(p.holiday_accrual)} />
              <Line label="Travel cost" value={eur(p.travel_cost)} />
              <Line label="Materials" value={eur(p.materials_cost)} />
              <Line label="Supplier costs" value={eur(p.supplier_costs || 0)} />
              <Line label="Other direct costs" value={eur(p.other_cost)} />
              <Line label={`Company running cost (${hrs(p.overhead_pct || 0)}%${p.overhead_source === "site" ? " · site rate" : ""})`} value={eur(p.overhead_cost || 0)} />
              <Line label="Total cost" value={eur(p.total_cost)} bold />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <KpiCard testId="pnl-hours" label="Labour Hours" value={hrs(p.total_hours)} />
            <KpiCard testId="pnl-rev-hr" label="Revenue / Hour" value={eur(p.revenue_per_hour)} />
            <KpiCard testId="pnl-cost-hr" label="Cost / Hour" value={eur(p.cost_per_hour)} />
            <KpiCard testId="pnl-profit-hr" label="Profit / Hour" value={eur(p.profit_per_hour)} />
          </div>
        </>
      )}
    </AdminLayout>
  );
}
